import React, { useEffect, useRef, useState } from "react";
import NavBar from "../components/NavBar.jsx";
import BrandMark from "../components/BrandMark.jsx";
import "../styles/bespoke.css";

const REVIEW_STEPS = [
  {
    number: "01 / UNDERSTAND",
    title: "Start with the concern",
    description: "We listen, establish context and identify the decision you need to make.",
  },
  {
    number: "02 / AGREE",
    title: "Set the boundaries",
    description: "We document what is in scope, the evidence needed, methods, exclusions and responsibilities.",
  },
  {
    number: "03 / ASSESS",
    title: "Examine what matters",
    description: "We review relevant people, devices, access, systems, information, suppliers and processes.",
  },
  {
    number: "04 / EXPLAIN",
    title: "Give you a clear path",
    description: "Your Security Blueprint explains the findings, uncertainty and proportionate next actions.",
  },
];

const REVIEW_FINDINGS = [
  ["What we examined", "The scope, evidence, methods and limits of the Review."],
  ["What we found", "What the evidence supports, why it matters and how it may affect you."],
  ["What remains uncertain", "Questions the available evidence could not resolve."],
  ["What to do next", "Prioritised actions, owners and dependencies to help you move forward."],
  ["Your choice of provider", "You can act yourself, work with AskMax or choose another provider."],
  ["Specialist depth when useful", "Technical appendices or addenda can support the people implementing the plan."],
];

const EMPTY_ENQUIRY = { name: "", businessName: "", email: "", service: "", message: "", privacyAcknowledged: false };
const LOCAL_ENQUIRY_PREVIEW = process.env.ASKMAX_BESPOKE_ENQUIRY_LOCAL_TEST === true;
const NETLIFY_FORMS_ENABLED = process.env.ASKMAX_BESPOKE_ENQUIRY_NETLIFY_ENABLED === true;
const ENQUIRY_FORM_ENABLED = LOCAL_ENQUIRY_PREVIEW || NETLIFY_FORMS_ENABLED;
const PRIVACY_NOTICE_URL = process.env.ASKMAX_BESPOKE_PRIVACY_NOTICE_URL || "";
const SERVICE_LABELS = {
  security_review: "Security Review",
  check_a_payment: "Check a Payment",
  php2shell: "PHP2Shell Exposure Assessment",
};

export default function Bespoke({ onNavigate, initialEnquiry, initialSection }) {
  const [enquiry, setEnquiry] = useState(EMPTY_ENQUIRY);
  const [enquiryState, setEnquiryState] = useState("idle");
  const submissionInFlight = useRef(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "AskMax Bespoke Security Review";
    return () => { document.title = previousTitle; };
  }, []);

  useEffect(() => {
    const section = initialSection || window.location.hash.slice(1);
    if (!["review", "services", "contact"].includes(section)) return;
    requestAnimationFrame(() => document.getElementById(section)?.scrollIntoView({ behavior: "auto", block: "start" }));
  }, [initialSection]);

  useEffect(() => {
    if (!initialEnquiry) return;
    setEnquiry((current) => ({
      ...current,
      service: initialEnquiry.service || current.service,
      message: initialEnquiry.message || current.message,
    }));
    requestAnimationFrame(() => document.getElementById("contact")?.scrollIntoView({ behavior: "auto", block: "start" }));
  }, [initialEnquiry]);

  const handleHomeClick = (event) => {
    event.preventDefault();
    onNavigate("/");
  };

  const chooseService = (service) => {
    setEnquiry((current) => ({ ...current, service }));
    setEnquiryState("idle");
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const updateEnquiry = (field) => (event) => {
    setEnquiry((current) => ({ ...current, [field]: event.target.value }));
    if (enquiryState !== "idle") setEnquiryState("idle");
  };

  const handleEnquirySubmit = async (event) => {
    event.preventDefault();
    if ((!LOCAL_ENQUIRY_PREVIEW && !NETLIFY_FORMS_ENABLED) || submissionInFlight.current) return;
    submissionInFlight.current = true;
    setEnquiryState("submitting");

    try {
      if (LOCAL_ENQUIRY_PREVIEW) {
        const response = await fetch("/api/bespoke-enquiry", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(enquiry),
        });
        const result = await response.json().catch(() => ({}));
        if (response.ok && result.status === "local_validation_only" && result.delivered === false) {
          setEnquiry(EMPTY_ENQUIRY);
          setEnquiryState("local-validation-complete");
          return;
        }
        setEnquiryState("failed");
        return;
      }

      const requestBody = JSON.stringify(enquiry);
      if (new TextEncoder().encode(requestBody).byteLength > 8192) {
        setEnquiryState("failed");
        return;
      }

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      let response;
      let result;
      try {
        response = await fetch("/api/bespoke-enquiry", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: requestBody,
          signal: controller.signal,
        });
        result = await response.json().catch(() => ({}));
      } finally {
        window.clearTimeout(timeout);
      }
      if (!response.ok || result.status !== "accepted") {
        setEnquiryState("failed");
        return;
      }
      setEnquiry(EMPTY_ENQUIRY);
      setEnquiryState("provider-accepted");
    } catch {
      setEnquiryState("failed");
    } finally {
      submissionInFlight.current = false;
    }
  };

  return (
    <>
      <NavBar onNavigate={onNavigate} mode="bespoke" onSelectService={chooseService} />
      <main className="bespoke-page">
        <section className="bespoke-page__hero container">
          <div className="bespoke-page__hero-copy">
            <p className="bespoke-page__eyebrow">AskMax Security / Bespoke</p>
            <h1>Cybersecurity, explained simply.</h1>
            <p className="bespoke-page__hero-lede">
              A knowledgeable, independent view for people and businesses. Understand website risks, security concerns and suspicious payment requests, then get a clear next step.
            </p>
            <a className="btn btn-primary" href="#services">Choose a service</a>
          </div>
          <aside className="bespoke-page__conversation-note">
            <strong>Start with the decision.</strong>
            <p>
              We begin by understanding your situation and what you need to decide. Any assessment is agreed in writing before technical access or testing begins.
            </p>
          </aside>
        </section>

        <section className="bespoke-page__section container" id="services" aria-labelledby="bespoke-services-title">
          <div className="bespoke-page__section-head">
            <h2 id="bespoke-services-title">Choose the help that fits.</h2>
            <p>Start with the question you need answered. We use your enquiry to understand the situation and agree any work before it begins.</p>
          </div>
          <div className="bespoke-page__services">
            <article className="bespoke-page__service">
              <p className="bespoke-page__service-label">BESPOKE CYBERSECURITY</p>
              <h3>Security Review</h3>
              <p>Understand the security concerns that matter to your organisation and get a practical plan for what to do next.</p>
              <button className="btn btn-primary" type="button" onClick={() => chooseService("security_review")}>
                Discuss a Security Review
              </button>
            </article>
            <article className="bespoke-page__service">
              <p className="bespoke-page__service-label">PAYMENT SECOND OPINION</p>
              <h3>Check a Payment</h3>
              <p>Get a second opinion on a suspicious invoice, changed supplier bank details or an unusual request to send money.</p>
              <p className="bespoke-page__payment-warning">Independently verify urgent payment instructions through a previously known number or separate trusted channel. Submitting an enquiry is not confirmation that a payment is safe.</p>
              <button className="btn btn-primary" type="button" onClick={() => onNavigate("/check-a-payment")}>
                Check a Payment
              </button>
            </article>
            <article className="bespoke-page__service">
              <p className="bespoke-page__service-label">URGENT SECURITY ADVISORY</p>
              <h3>PHP2Shell Exposure Assessment</h3>
              <p>Not sure whether your website uses PHP or has the relevant updates? Learn what to check and ask about a scoped assessment.</p>
              <p className="bespoke-page__service-price">€299 excluding applicable VAT / enquiry only</p>
              <button className="btn btn-primary" type="button" onClick={() => onNavigate("/php2shell")}>
                Check My Website&apos;s Risk
              </button>
            </article>
          </div>
        </section>

        <section className="bespoke-page__section container" id="review" aria-labelledby="bespoke-review-title">
          <div className="bespoke-page__section-head">
            <h2 id="bespoke-review-title">One clear review, shaped around your situation.</h2>
            <p>We help you understand how relevant security concerns could affect your organisation and what practical steps may help. First, we agree exactly what you want reviewed and what evidence is available.</p>
          </div>
          <div className="bespoke-page__steps" aria-label="Review process">
            {REVIEW_STEPS.map((step) => (
              <article className="bespoke-page__step" key={step.number}>
                <p className="bespoke-page__step-number">{step.number}</p>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
          <div className="php2shell-page__hero-actions">
            <button className="btn btn-primary" type="button" onClick={() => chooseService("security_review")}>
              Enquire about a Security Review
            </button>
          </div>
        </section>

        <section className="bespoke-page__section container" aria-labelledby="bespoke-decisions-title">
          <div className="bespoke-page__section-head">
            <h2 id="bespoke-decisions-title">Know what was checked, and what to do next.</h2>
            <p>
              Your findings should make sense without needing to be a security specialist. We explain what the evidence supports, what remains uncertain and which actions to consider.
            </p>
          </div>
          <ul className="bespoke-page__findings">
            {REVIEW_FINDINGS.map(([title, description]) => (
              <li key={title}>
                <strong>{title}</strong>
                <span>{description}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="bespoke-page__section container" aria-labelledby="bespoke-blueprint-title">
          <div className="bespoke-page__blueprint">
            <div>
              <p className="bespoke-page__eyebrow">Your principal output</p>
              <h2 id="bespoke-blueprint-title">A clear plan you can use.</h2>
              <p>Written in plain language, with more technical detail only where it helps.</p>
            </div>
            <ul>
              <li>What we agreed to review</li>
              <li>What we found in the evidence</li>
              <li>What we could not confirm</li>
              <li>Practical next steps, in priority order</li>
              <li>Extra technical detail where useful</li>
            </ul>
          </div>
          <p className="bespoke-page__limitation">
            The Review is a diagnostic assessment. It does not certify security, guarantee that every threat will be found, or promise that incidents or losses will be prevented.
          </p>
        </section>

        <section className="bespoke-page__section container" aria-labelledby="bespoke-independence-title">
          <div className="bespoke-page__section-head">
            <h2 id="bespoke-independence-title">Independent by design.</h2>
            <p>We explain what the evidence shows without overstating risk. Any testing or follow-up work must be separately discussed, scoped and authorised in writing.</p>
          </div>
        </section>

        <section className="bespoke-page__conversation container" id="contact" aria-labelledby="bespoke-contact-title">
          <div>
            <h2 id="bespoke-contact-title">Contact AskMax</h2>
            <p>Tell us briefly what you need help with. No work begins until scope and written authority are agreed.</p>
          </div>
          <div className="bespoke-page__conversation-form">
            <form name="askmax-enquiry" method="POST" action="/" data-netlify="true" netlify-honeypot="website" aria-describedby="bespoke-enquiry-purpose" onSubmit={handleEnquirySubmit} autoComplete="on">
              <p className="bespoke-page__form-label">ENQUIRY FORM</p>
              <p className="bespoke-page__form-note" id="bespoke-enquiry-purpose">Name, email, service and message are required. Business name is optional. This is not marketing consent or a booking.</p>
              {!ENQUIRY_FORM_ENABLED && (
                <p className="bespoke-page__form-note" role="status">
                  Enquiry submission is not enabled in this build. No details should be entered or sent.
                </p>
              )}
              {process.env.ASKMAX_BESPOKE_ENQUIRY_LOCAL_TEST === true && enquiryState === "local-validation-complete" && (
                <p className="bespoke-page__form-status" role="status">
                  Local form validation completed. No enquiry was sent or saved.
                </p>
              )}
              {enquiryState === "failed" && (
                <p className="bespoke-page__form-error" role="alert">
                  We could not send your enquiry right now. Please try again.
                </p>
              )}
              {enquiryState === "provider-accepted" && (
                <p className="bespoke-page__form-status" role="status">
                  Thank you. Your enquiry has been sent.
                </p>
              )}
              <div className="bespoke-page__form-fields">
                <input type="hidden" name="form-name" value="askmax-enquiry" />
                <input type="hidden" name="subject" value={`AskMax Security Enquiry — ${SERVICE_LABELS[enquiry.service] || ""}`} />
                <label className="bespoke-page__field">
                  <span>Name</span>
                  <input autoComplete="name" maxLength={120} name="name" required value={enquiry.name} onChange={updateEnquiry("name")} disabled={!ENQUIRY_FORM_ENABLED} />
                </label>
                <label className="bespoke-page__field">
                  <span>Business name (optional)</span>
                  <input autoComplete="organization" maxLength={120} name="businessName" value={enquiry.businessName} onChange={updateEnquiry("businessName")} disabled={!ENQUIRY_FORM_ENABLED} />
                </label>
                <label className="bespoke-page__field">
                  <span>Email address</span>
                  <input autoComplete="email" maxLength={254} name="email" required type="email" value={enquiry.email} onChange={updateEnquiry("email")} disabled={!ENQUIRY_FORM_ENABLED} />
                </label>
                <label className="bespoke-page__field">
                  <span>Service required</span>
                  <select name="service" required value={enquiry.service} onChange={updateEnquiry("service")} disabled={!ENQUIRY_FORM_ENABLED}>
                    <option value="">Select a service</option>
                    <option value="security_review">Security Review</option>
                    <option value="check_a_payment">Check a Payment</option>
                    <option value="php2shell">PHP2Shell Exposure Assessment</option>
                  </select>
                </label>
                {enquiry.service === "check_a_payment" && (
                  <p className="bespoke-page__payment-form-warning" role="note">
                    Do not include passwords, banking credentials, security codes or sensitive financial documents. Independently verify urgent payment instructions through a previously known number or separate trusted channel. Submitting an enquiry is not confirmation that a payment is safe.
                  </p>
                )}
                <label className="bespoke-page__field bespoke-page__field--wide">
                  <span id="bespoke-message-label">Message</span>
                  <textarea aria-labelledby="bespoke-message-label" maxLength={3000} name="message" required rows={4} value={enquiry.message} onChange={updateEnquiry("message")} disabled={!ENQUIRY_FORM_ENABLED} />
                </label>
                <label className="bespoke-page__consent bespoke-page__field--wide">
                  <input type="checkbox" name="privacyAcknowledged" required checked={enquiry.privacyAcknowledged} onChange={(event) => setEnquiry((current) => ({ ...current, privacyAcknowledged: event.target.checked }))} disabled={!ENQUIRY_FORM_ENABLED} />
                  <span>
                    I have read and understand the <a href={PRIVACY_NOTICE_URL || "/privacy/"}>privacy notice</a> for handling this enquiry.
                  </span>
                </label>
                <div className="bespoke-page__honeypot" aria-hidden="true">
                  <label htmlFor="bespoke-website">Leave this field empty</label>
                  <input autoComplete="off" id="bespoke-website" name="website" tabIndex={-1} value="" onChange={() => {}} />
                </div>
              </div>
              {process.env.ASKMAX_BESPOKE_ENQUIRY_LOCAL_TEST === true ? (
                <p className="bespoke-page__form-note">
                  Local test only: synthetic details are sent to the local development server for validation, not delivered or saved. Do not enter real personal information.
                </p>
              ) : !ENQUIRY_FORM_ENABLED ? (
                <p className="bespoke-page__form-note">
                  A reviewed privacy notice and approved delivery destination are required before real enquiries can be enabled. Do not enter personal information while this form is disabled.
                </p>
              ) : null}
              <button className="btn btn-primary" type="submit" disabled={!ENQUIRY_FORM_ENABLED || enquiryState === "submitting"}>
                {enquiryState === "submitting" ? "Submitting enquiry…" : "Send enquiry"}
              </button>
            </form>
          </div>
        </section>

        <section className="bespoke-page__close container">
          <p>
            Our team will review your enquiry, agree the scope of work and explain the next steps before any assessment begins. Written authorisation is agreed before technical work starts.
          </p>
        </section>
      </main>

      <footer className="bespoke-page__footer">
        <div className="container bespoke-page__footer-row">
          <BrandMark onClick={handleHomeClick} />
          <span>AskMax Bespoke Security Review</span>
          <a href="/legal-notice" onClick={(event) => { event.preventDefault(); onNavigate("/legal-notice"); }}>Legal Notice</a>
          <a href="/privacy/" onClick={(event) => { event.preventDefault(); onNavigate("/privacy"); }}>Privacy Notice</a>
          <span>Scope and timing are agreed before work begins.</span>
        </div>
      </footer>
    </>
  );
}
